import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, ExternalLink } from 'lucide-react';
import * as pdfjs from 'pdfjs-dist';

// Served as a static JS asset by Amplify; prepared from the installed pdfjs-dist version.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js?v=${encodeURIComponent(pdfjs.version)}`;

/** One-page-at-a-time PDF.js viewer. HTTP range loading avoids whole-file downloads. */
export default function PdfPreviewDrawer({ title, url, startPage = 1, endPage = 1, onClose }) {
  const [page, setPage] = useState(startPage);
  const [zoom, setZoom] = useState(100);
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const canvas = useRef(null);

  useEffect(() => {
    let live = true;
    setPage(startPage);
    setDocument(null);
    setLoading(true);
    setError('');
    const task = pdfjs.getDocument({
      url,
      withCredentials: false,
      disableStream: true,
      disableAutoFetch: true,
      rangeChunkSize: 131072,
    });
    task.promise.then((pdf) => {
      if (!live) return;
      setDocument(pdf);
      setLoading(false);
    }).catch((err) => { if (live) { setLoading(false); setError(err?.message || 'Unable to load PDF'); } });
    return () => {live=false;task.destroy();};
  }, [url, startPage]);

  useEffect(() => {
    if (!document || !canvas.current) return undefined;
    let cancelled = false;
    let task;
    setLoading(true);
    const draw = async () => {
      try {
        const pdfPage = await document.getPage(page);
        if (cancelled || !canvas.current) return;
        const viewport = pdfPage.getViewport({ scale: zoom / 100 });
        const ratio = Math.min(2, window.devicePixelRatio || 1);
        const surface = canvas.current;
        surface.width = Math.round(viewport.width * ratio);
        surface.height = Math.round(viewport.height * ratio);
        surface.style.width = `${viewport.width}px`;
        surface.style.height = `${viewport.height}px`;
        const context = surface.getContext('2d', { alpha: false });
        task = pdfPage.render({ canvasContext: context, viewport, transform: ratio === 1 ? null : [ratio,0,0,ratio,0,0] });
        await task.promise;
        if (!cancelled) setLoading(false);
      } catch (err) {
        if (!cancelled && err?.name !== 'RenderingCancelledException') {
          setError(err?.message || 'Unable to render PDF page');setLoading(false);
        }
      }
    };
    draw();
    return () => {cancelled=true;task?.cancel();};
  }, [page, zoom, document]);

  const maxPage = Math.min(endPage, document?.numPages || endPage);
  const go = (n) => setPage(Math.max(startPage, Math.min(maxPage, n)));
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="flex h-full w-full max-w-4xl flex-col bg-white shadow-xl" role="dialog" aria-modal="true" aria-label="PDF preview">
        <header className="flex items-center gap-2 border-b px-4 py-3">
          <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">{title}</h3>
          <button className="rounded p-1 hover:bg-gray-100" onClick={() => window.open(url, '_blank', 'noopener,noreferrer')} title="Open in browser"><ExternalLink size={17}/></button>
          <button className="rounded p-1 hover:bg-gray-100" onClick={onClose} aria-label="Close preview"><X size={18}/></button>
        </header>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-gray-50 px-4 py-2 text-xs">
          <div className="flex items-center gap-2">
            <button onClick={() => go(page-1)} disabled={page<=startPage} aria-label="Previous page" className="disabled:opacity-40"><ChevronLeft size={18}/></button>
            <span>Page</span>
            <input type="number" min={startPage} max={maxPage} className="w-16 rounded border p-1 text-center" value={page} onChange={(e) => go(Number(e.target.value)||startPage)}/>
            <span>of {maxPage}</span>
            <button onClick={() => go(page+1)} disabled={page>=maxPage} aria-label="Next page" className="disabled:opacity-40"><ChevronRight size={18}/></button>
          </div>
          <div className="flex items-center gap-2">
            <span>Zoom</span><button aria-label="Zoom out" onClick={() => setZoom((n) => Math.max(50,n-25))}><ZoomOut size={17}/></button>
            <span>{zoom}%</span><button aria-label="Zoom in" onClick={() => setZoom((n) => Math.min(250,n+25))}><ZoomIn size={17}/></button>
          </div>
        </div>
        <div className="relative min-h-0 flex-1 overflow-auto bg-gray-200 p-4 text-center">
          {error && <p className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          {loading && <p className="absolute left-3 top-3 rounded bg-white px-3 py-1 text-xs text-gray-600 shadow">Loading page…</p>}
          <canvas ref={canvas} className="mx-auto bg-white shadow" />
        </div>
        <p className="border-t px-3 py-1.5 text-xs text-gray-500">Pages render on demand; the source file and page fidelity are preserved.</p>
      </section>
    </div>
  );
}
